/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { L10nService, VIPModule } from '@vmw/ngx-vip';
import { sortBy, values } from 'lodash';
import { AviDropdownComponent, IAviDropdownOption } from 'standalone-components/avi-dropdown';
import { Timeframe } from 'ng/root-store/user-preferences/user-preferences.state';
import { selectTimeframe, updateUIProperty } from 'ng/root-store/user-preferences';

import * as l10n from './timeframe-selector.l10n';
import './timeframe-selector.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * The timeframe change event triggering reason (change from this selector) passed to the legacy
 * Timeframe service.
 */
const selectorTfChangeReason = 'from timeframeSelector';

interface ILegacyTimeframeOptionValue {
    index: number;
    // key - the timeframe value to the option, eg. '6h'
    key: Timeframe;
    label: string;
    step?: number;
    limit?: number;
    interval?: number;
    range?: number;
    timeTickFormat?: Array<Array<Record<string, any>>>;
    hiddenInLayout?: boolean;
}

type TLegacyTimeframeOption = Record<Timeframe, ILegacyTimeframeOptionValue>;

const isTimeframeAvail = (option: ILegacyTimeframeOptionValue): boolean => !option.hiddenInLayout;

/**
 * @description
 *     Timeframe dropdown component. Connected to the root store and updates the timeframe
 *     UI Property to persist the state. Also the legacy Timeframe Service is bound to this
 *     component to provide dropdown options and sync current selected value. The synced selected
 *     value in the legacy timeframe service will trigger a change event and CollDataSource listens
 *     to that to keep collection data updated based on the latest timeframe.
 *     TODO: AV-179217. The binding with Timeframe Service should be deprecated when it's decoupled
 *     from CollDataSource. Then the store should be used to track/trigger collection changes.
 * @author alextsg, Zhiqian Liu
 */
@Component({
    selector: 'timeframe-selector',
    templateUrl: './timeframe-selector.component.html',
    standalone: true,
    imports: [
        VIPModule,
        AviDropdownComponent,
        FormsModule,
    ],
})
export class TimeframeSelectorComponent {
    /**
     * Timeframe dropdown options.
     */
    public options: Array<IAviDropdownOption<Timeframe>>;

    public timeframe: Timeframe = Timeframe.PAST_SIX_HOURS;

    public readonly l10nKeys = l10nKeys;

    /**
     * Subscription to the timeframe property and watches for updates, which will update the
     * dropdown value.
     */
    private selectTimeframeSubscription: Subscription;

    constructor(
        @Inject('Timeframe')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        private readonly timeframeService: any,
        private readonly l10nService: L10nService,
        private readonly store: Store,
    ) {
        l10nService.registerSourceBundles(dictionary);

        // set default options
        this.options = [
            {
                value: Timeframe.REAL_TIME,
                label: l10nService.getMessage(l10nKeys.past30minutesOptionLabel),
            },
            {
                value: Timeframe.PAST_SIX_HOURS,
                label: l10nService.getMessage(l10nKeys.past6HoursOptionLabel),
            },
            {
                value: Timeframe.PAST_DAY,
                label: l10nService.getMessage(l10nKeys.pastDayOptionLabel),
            },
            {
                value: Timeframe.PAST_WEEK,
                label: l10nService.getMessage(l10nKeys.pastWeekOptionLabel),
            },
            {
                value: Timeframe.PAST_MONTH,
                label: l10nService.getMessage(l10nKeys.pastMonthOptionLabel),
            },
            {
                value: Timeframe.PAST_QUARTER,
                label: l10nService.getMessage(l10nKeys.pastQuarterOptionLabel),
            },
            {
                value: Timeframe.PAST_YEAR,
                label: l10nService.getMessage(l10nKeys.pastYearOptionLabel),
            },
        ];
    }

    public ngOnInit(): void {
        this.selectTimeframeSubscription = this.store.select(selectTimeframe)
            .subscribe(timeframe => this.timeframe = timeframe);

        this.timeframeService.on('change', this.onTimeframeChangeFromTheService);
        this.timeframeService.on('afterGroupChange', this.onTimeframeGroupChange);
    }

    /**
     * Handle dropdown changes and update the timeframe UI Property and the selected value in the
     * legacy timeframe service.
     */
    public onTimeframeChangeFromTheSelector(timeframe: Timeframe): void {
        this.store.dispatch(updateUIProperty({
            payload: {
                timeframe,
            },
        }));

        this.timeframeService.set(timeframe, selectorTfChangeReason);
    }

    /**
     * Handle timeframe changing events from the legacy timeframe service, which is triggered
     * programmatically usually because of setting custom timeframe. This sets the store with a new
     * UI Property timeframe value and also results in syncing the selector dropdown value with the
     * store updates.
     */
    public onTimeframeChangeFromTheService(reason: string): void {
        if (reason !== selectorTfChangeReason) {
            const timeframe = this.timeframeService.selected().key as Timeframe;

            this.store.dispatch(updateUIProperty({
                payload: {
                    timeframe,
                },
            }));
        }
    }

    /**
     * Handle the timeframe group change event from the legacy timeframe service.
     * Update the dropdown options as the group changes.
     */
    public onTimeframeGroupChange(): void {
        // filter timeframe options based on isAvailable and sort them based on index as
        // lodash.values doesn't guarantee orders
        const options = sortBy(
            values(
                this.timeframeService.options as TLegacyTimeframeOption,
            ).filter(isTimeframeAvail),
            'index',
        );

        // mapping timeframe options to create dropdown options
        this.options = options.map(
            ({ key, label }) => {
                return {
                    value: key,
                    // this is reading l10n labels from the legacy l10n keys
                    // eg. ajs-legacy.time-frame.past30minutesOptionLabel
                    label: this.l10nService.getMessage(label),
                };
            },
        );
    }

    /** @override */
    public ngOnDestroy(): void {
        this.selectTimeframeSubscription.unsubscribe();
        this.timeframeService.unbind('change', this.onTimeframeChangeFromTheService);
        this.timeframeService.unbind('afterGroupChange', this.onTimeframeGroupChange);
    }
}
